import React, { useState, useMemo, useEffect } from 'react';
import {
  Search, ArrowRight, ArrowLeft, Loader2, AlertTriangle, Database, Clock, Layers, X,
} from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useQuery } from '../../../analytics-shared/hooks/queryShim';
import { metadataApi } from '../../../analytics-shared/api/metadataApi';
import DataSourcePicker, { summarySentence } from '../../../analytics-shared/components/DataSourcePicker';

/**
 * Create-report flow — guided, HubSpot-style.
 *
 *   Step 1  Pick ONE primary object (searchable grid, recently used
 *           surfaced first). No auto-generated combination list.
 *   Step 2  Optionally add up to 3 related objects. Only objects with a
 *           real relationship path are offered; each hop shows its
 *           relationship and a "with / with or without" join choice.
 *
 * onStart contract (unchanged from the legacy modal):
 *   onStart(primaryObjectKey, reportType)
 * reportType carries {id, kind, name, description, primary, related[]}
 * where related entries include anchor + join for the engine.
 */

const RECENT_KEY = 'cluvik.reports.recentPrimaries';

const readRecents = () => {
  try { return JSON.parse(localStorage.getItem(RECENT_KEY)) || []; } catch { return []; }
};
const pushRecent = (key) => {
  try {
    const next = [key, ...readRecents().filter((k) => k !== key)].slice(0, 6);
    localStorage.setItem(RECENT_KEY, JSON.stringify(next));
  } catch { /* ignore */ }
};

const typeName = (primary, related) => {
  if (!related?.length) return primary.label;
  const bits = related.map((r) => r.plural || r.label);
  if (bits.length === 1) return `${primary.plural || primary.label} with ${bits[0]}`;
  return `${primary.plural || primary.label} with ${bits.slice(0, -1).join(', ')} and ${bits[bits.length - 1]}`;
};

const buildReportType = (primary, related) => ({
  id: `guided/${primary.key}/${(related || []).map((r) => `${r.direction}.${r.key}.${r.via}`).join('+') || 'single'}`,
  kind: related?.length ? 'cross' : 'single',
  name: typeName(primary, related),
  description: summarySentence(primary, related),
  primary: { key: primary.key, label: primary.label, plural: primary.plural },
  related: related || [],
});

const CreateReportModal = ({ open, onClose, onStart }) => {
  const [step, setStep] = useState(1);
  const [search, setSearch] = useState('');
  const [primary, setPrimary] = useState(null);
  const [related, setRelated] = useState([]);
  const [starting, setStarting] = useState(false);

  const graphQuery = useQuery({
    queryKey: ['report-graph'],
    queryFn: () => metadataApi.getReportGraph(),
    enabled: open,
    staleTime: 60_000,
  });
  const graph = graphQuery.data || { objects: [], relations: {} };

  useEffect(() => {
    if (open) {
      setStep(1); setSearch(''); setPrimary(null); setRelated([]); setStarting(false);
    }
  }, [open]);

  const recents = useMemo(() => readRecents(), [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const filteredObjects = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = q
      ? graph.objects.filter((o) => `${o.label} ${o.plural} ${o.key}`.toLowerCase().includes(q))
      : graph.objects;
    return list;
  }, [graph.objects, search]);

  const recentObjects = useMemo(
    () => recents.map((k) => graph.objects.find((o) => o.key === k)).filter(Boolean),
    [recents, graph.objects],
  );

  const choosePrimary = (obj) => {
    setPrimary(obj);
    setRelated([]);
    setStep(2);
  };

  const handleStart = async () => {
    if (!primary || starting) return;
    setStarting(true);
    try {
      pushRecent(primary.key);
      await onStart(primary.key, buildReportType(primary, related));
    } finally {
      setStarting(false);
    }
  };

  const relationCount = (key) => (graph.relations?.[key] || []).length;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl p-0 overflow-hidden gap-0">
        <DialogHeader className="px-6 py-4 border-b border-slate-200">
          <DialogTitle className="text-xl text-slate-900">
            {step === 1 ? 'Create New Report' : 'Choose your data'}
          </DialogTitle>
          <DialogDescription>
            {step === 1
              ? 'Start by choosing the main object your report is about. You can add related objects next.'
              : 'Optionally add related objects. Only objects connected to your selection are shown.'}
          </DialogDescription>
        </DialogHeader>

        {step === 1 && (
          <div className="flex flex-col" style={{ height: 480 }}>
            <div className="p-3 border-b border-slate-200">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input autoFocus placeholder="Search objects…" value={search}
                  onChange={(e) => setSearch(e.target.value)} className="pl-8 h-9" />
                {search && (
                  <button onClick={() => setSearch('')} aria-label="Clear search"
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-4">
              {graphQuery.isLoading && (
                <div className="h-full flex items-center justify-center text-sm text-slate-500">
                  <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading your objects…
                </div>
              )}
              {graphQuery.error && (
                <div className="h-full flex flex-col items-center justify-center text-center">
                  <AlertTriangle className="h-6 w-6 text-amber-500 mb-2" />
                  <p className="text-sm text-slate-700 mb-1">Couldn't load CRM schema</p>
                  <p className="text-xs text-slate-500 mb-3">{graphQuery.error.message || 'Unknown error'}</p>
                  <Button size="sm" variant="outline" onClick={graphQuery.refetch}>Retry</Button>
                </div>
              )}
              {!graphQuery.isLoading && !graphQuery.error && (
                <>
                  {!search && recentObjects.length > 0 && (
                    <>
                      <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-2">
                        <Clock className="h-3 w-3" /> Recently used
                      </div>
                      <div className="grid grid-cols-3 gap-2 mb-4">
                        {recentObjects.map((o) => (
                          <ObjectCard key={`r-${o.key}`} obj={o} relCount={relationCount(o.key)} onClick={() => choosePrimary(o)} />
                        ))}
                      </div>
                    </>
                  )}
                  <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-2">
                    <Database className="h-3 w-3" /> All objects
                  </div>
                  {filteredObjects.length === 0 ? (
                    <div className="py-10 text-center text-sm text-slate-400">
                      {graph.objects.length === 0
                        ? 'No CRM objects are available for this tenant yet.'
                        : 'No objects match your search.'}
                    </div>
                  ) : (
                    <div className="grid grid-cols-3 gap-2">
                      {filteredObjects.map((o) => (
                        <ObjectCard key={o.key} obj={o} relCount={relationCount(o.key)} onClick={() => choosePrimary(o)} />
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}

        {step === 2 && primary && (
          <div className="flex flex-col" style={{ height: 480 }}>
            <div className="flex-1 overflow-y-auto p-5">
              <DataSourcePicker
                graph={graph}
                primary={primary}
                related={related}
                onChange={setRelated}
              />
            </div>
            <div className="px-5 py-4 border-t border-slate-200 bg-slate-50 flex items-center gap-2">
              <Button variant="outline" onClick={() => { setStep(1); setPrimary(null); setRelated([]); }}>
                <ArrowLeft className="h-4 w-4 mr-1.5" /> Change object
              </Button>
              <div className="flex-1" />
              <Button onClick={handleStart} disabled={starting} className="bg-indigo-600 hover:bg-indigo-700">
                {starting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                Start Report
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const ObjectCard = ({ obj, relCount, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="group flex items-start gap-2.5 rounded-xl border border-slate-200 bg-white p-3 text-left hover:border-indigo-300 hover:shadow-sm transition-all"
  >
    <div className="p-2 rounded-lg bg-slate-100 group-hover:bg-indigo-600 transition-colors flex-shrink-0">
      <Database className="h-4 w-4 text-slate-600 group-hover:text-white transition-colors" />
    </div>
    <div className="min-w-0 flex-1">
      <div className="flex items-center gap-1.5">
        <span className="text-sm font-medium text-slate-900 truncate">{obj.label}</span>
        {obj.is_custom && (
          <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 flex-shrink-0">
            <Layers className="h-2.5 w-2.5 inline mr-0.5 -mt-px" />Custom
          </span>
        )}
      </div>
      <div className="text-[11px] text-slate-400 mt-0.5">
        {obj.field_count || 0} fields{relCount ? ` · ${relCount} relationship${relCount === 1 ? '' : 's'}` : ''}
      </div>
    </div>
  </button>
);

export default CreateReportModal;
